/* PUBLIC TRUCK TRACKER — one truck, all day, permanent link. Built for the pump crew
   paired with that truck: every load it runs today, who it's for, where it is and when
   it'll land. No login. */
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Truck, MapPin, Clock, RefreshCw, Phone, Gauge, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20_000;
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null);
const ROLLING = ["en_route", "on_site", "discharging", "returning", "loading", "slumping"];

export default function TruckTracker() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/public/truck/${token}`)).data); setErr(""); }
    catch (e) { setErr(e.response?.status === 404 ? "This tracking link isn't valid" : "Couldn't load the truck right now"); }
  }, [token]);
  useEffect(() => { load(); const t = setInterval(load, POLL_MS); return () => clearInterval(t); }, [load]);

  const t = d?.truck;
  const jobs = d?.jobs || [];

  return (
    <div className="contractor-theme min-h-screen bg-[#0B0D10] text-zinc-200 pb-12" data-testid="truck-tracker">
      <div className="sticky top-0 z-10 bg-[#0B0D10]/95 backdrop-blur border-b border-zinc-800 px-4 py-3">
        <div className="max-w-3xl mx-auto flex items-center gap-3">
          <div className="w-9 h-9 bg-[#F57C00] flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-black" /></div>
          <div className="min-w-0">
            <div className="heading text-lg font-black leading-none !text-white">ARMOR<span className="text-[#F57C00]">BATCH</span> GLOBAL</div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#F57C00]">
              Truck tracker{d?.supplier?.brand || d?.supplier?.name ? ` · loads from ${(d.supplier.brand || d.supplier.name).toUpperCase()}` : ""}
            </div>
          </div>
          <button type="button" onClick={load} data-testid="truck-tracker-refresh" className="ml-auto mono text-[10px] uppercase tracking-widest border border-zinc-700 px-2 py-1.5 text-zinc-400 hover:text-white inline-flex items-center gap-1"><RefreshCw className="w-3 h-3" /> Refresh</button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-5 space-y-4">
        {err && <div className="border border-red-800 bg-red-950/40 text-red-300 p-4 mono text-sm" data-testid="truck-tracker-error"><AlertTriangle className="w-4 h-4 inline mr-2" />{err}</div>}

        {t && (
          <div className="border border-zinc-800 bg-[#11141A] p-4">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="heading text-4xl font-black !text-white leading-none" data-testid="truck-tracker-number">{t.number}</span>
              {t.rego && <span className="mono text-xs uppercase tracking-widest text-zinc-500">{t.rego}</span>}
              <span className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border ${ROLLING.includes(t.status) ? "border-emerald-600 text-emerald-400" : "border-zinc-700 text-zinc-500"}`} data-testid="truck-tracker-status">
                {(t.status || "idle").replace(/_/g, " ")}
              </span>
              <span className="ml-auto text-right">
                <span className="mono text-[9px] uppercase tracking-[0.25em] text-zinc-500 block">Today</span>
                <span className="heading text-2xl font-black !text-white" data-testid="truck-tracker-today">{d.today.loads} load{d.today.loads === 1 ? "" : "s"} · {d.today.m3} m³</span>
              </span>
            </div>
            <div className="mono text-[11px] text-zinc-400 mt-2 flex flex-wrap gap-x-5 gap-y-1">
              {t.driver_name && <span>Driver · <span className="text-zinc-200">{t.driver_name}</span></span>}
              {t.capacity_m3 ? <span className="inline-flex items-center gap-1"><Gauge className="w-3 h-3" /> {t.capacity_m3} m³</span> : null}
              {t.location_updated_at && <span>GPS {hhmm(t.location_updated_at)}</span>}
              {d.today.last_loaded_at && <span>Last batched {hhmm(d.today.last_loaded_at)}</span>}
            </div>
          </div>
        )}

        {d && jobs.length === 0 && !err && (
          <div className="border border-dashed border-zinc-800 p-8 text-center mono text-sm text-zinc-500" data-testid="truck-tracker-empty">
            Nothing on this truck today. Keep the link — it's permanent, so check back tomorrow.
          </div>
        )}

        {jobs.map((j) => (
          <div key={j.id} className="border border-zinc-800 bg-[#11141A]" data-testid={`truck-tracker-job-${j.id}`}>
            <div className="px-4 py-2 border-b border-zinc-800 flex items-center gap-2 flex-wrap">
              <span className="mono text-sm text-[#F57C00] font-black inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{j.time_required || "—"}</span>
              <span className="mono text-[11px] uppercase tracking-widest text-zinc-400">{j.customer_name}</span>
              <span className="ml-auto mono text-sm font-black !text-white">{j.delivered_m3 ?? 0} / {j.quantity_m3 ?? 0} m³</span>
            </div>
            <div className="px-4 py-3">
              <div className="text-sm font-bold text-white inline-flex items-start gap-1.5"><MapPin className="w-4 h-4 text-[#F57C00] mt-0.5 shrink-0" />{j.site_name || j.site_address || "Site"}</div>
              {j.site_address && <div className="mono text-[11px] text-zinc-500 pl-5">{j.site_address}</div>}
              <div className="mono text-[10px] uppercase tracking-widest text-zinc-400 mt-1 pl-5">{j.mix_code || "—"}{j.slump ? ` · ${j.slump} mm` : ""}</div>
              {j.site_contact_phone && (
                <a href={`tel:${j.site_contact_phone}`} className="mono text-[11px] text-[#38BDF8] inline-flex items-center gap-1 mt-2 pl-5"><Phone className="w-3 h-3" /> {j.site_contact_name || "Site contact"} · {j.site_contact_phone}</a>
              )}
            </div>
            {(j.trucks || []).map((tk, i) => (
              <div key={i} className="border-t border-zinc-800 px-4 py-2 flex items-center gap-2 flex-wrap mono text-[11px]">
                <span className={`uppercase tracking-widest px-1.5 py-0.5 border ${ROLLING.includes(tk.status) ? "border-emerald-600 text-emerald-400" : "border-zinc-700 text-zinc-500"}`}>{(tk.status || "").replace(/_/g, " ")}</span>
                {tk.load_m3 ? <span className="text-zinc-400">{tk.load_m3} m³ on board</span> : null}
                {tk.loaded_at && <span className="text-zinc-500">batched {hhmm(tk.loaded_at)}</span>}
                <span className="ml-auto text-right">
                  {tk.eta?.to_site ? <span className="text-[#38BDF8] font-black" data-testid="truck-tracker-eta">ETA {tk.eta.to_site}{tk.eta.to_site_mins != null ? ` · ${tk.eta.to_site_mins} min` : ""}</span>
                    : tk.distance_km != null ? <span className="text-zinc-500">{tk.distance_km} km away</span>
                      : <span className="text-zinc-600">no GPS</span>}
                </span>
              </div>
            ))}
          </div>
        ))}

        {d && <div className="mono text-[10px] uppercase tracking-widest text-zinc-600 text-center pt-2">Updates every 20 seconds · {d.date}</div>}
      </div>
    </div>
  );
}
